import React, { memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { useAppSelector } from 'store/hooks';
import BlurAmountView from 'design-system/BlurAmountView';
import EmojiText from 'design-system/EmojiText';
import IconArrowsBold from 'design-system/icons/IconArrowsBold';
import IconDeposit from 'design-system/icons/IconDeposit';
import IconWithdrawal from 'design-system/icons/IconWithdrawal';
import Spacer from 'design-system/Spacer';
import { SpaceUserAvatar } from 'design-system/SpaceUserAvatar';
import StatusIcon from 'design-system/StatusIcon';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { formatDateTime, formatNextBuyDate } from 'features/invest/utils';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { isWeb } from '../../../constants';
const STOCK_ICON_SIZE = 44;
const PotActivity = ({ pot, eta, type, title, emoji, status, amount, bankName, timestamp, productId, depositEta, formattedDate, depositDetails, isBankTransfer, formattedAmount, hideSpaceUserAvatar, forceShowPositiveAmount, bounceCard, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const navigation = useMainStackNavigation();
    const iconInfo = useMemo(() => {
        const infoObj = {
            isDeposit: true,
            icon: <IconDeposit size={STOCK_ICON_SIZE}/>,
        };
        switch (type) {
            case 'WITHDRAWAL':
                infoObj.isDeposit = false;
                infoObj.icon = <IconWithdrawal size={STOCK_ICON_SIZE}/>;
                break;
            case 'TRANSFER':
                if (!forceShowPositiveAmount) {
                    infoObj.isDeposit = false;
                }
                infoObj.icon = <IconArrowsBold size={STOCK_ICON_SIZE}/>;
                break;
            case 'INTEREST_PAYMENT':
                infoObj.isDeposit = false;
                infoObj.icon = <IconArrowsBold size={STOCK_ICON_SIZE}/>;
                break;
            default:
                break;
        }
        if (emoji) {
            infoObj.icon = <EmojiText fontSize={20}>{emoji}</EmojiText>;
        }
        return infoObj;
    }, [emoji, forceShowPositiveAmount, type]);
    const handlePress = useCallback(() => {
        if (emoji) {
            if (eta && bankName) {
                navigation.navigateDeprecated('WithdrawTimeline', {
                    emoji,
                    bankName,
                    productId,
                    isBankTransfer,
                    potName: pot.name,
                    dateString: formatNextBuyDate(eta),
                    dateTimeString: formatDateTime(eta),
                    withdrawAmount: Math.abs(amount.value),
                    formattedInitiatedTime: formatNextBuyDate(timestamp),
                });
                return;
            }
            if (depositEta && emoji) {
                navigation.navigateDeprecated('DepositTimeline', {
                    emoji,
                    timestamp,
                    depositEta,
                    isBankTransfer,
                    potName: pot.name,
                    depositDetails,
                    amount: Math.abs(amount.value),
                    potProductId: pot.productId,
                });
            }
        }
    }, [
        amount.value,
        bankName,
        depositDetails,
        depositEta,
        emoji,
        eta,
        isBankTransfer,
        pot.name,
        pot.productId,
        productId,
        timestamp,
    ]);
    const disabled = depositEta ? !emoji : !emoji || !eta || !bankName;
    const content = (<View style={styles.itemContainer}>
      <View style={styles.iconContainer}>{iconInfo.icon}</View>
      <View style={styles.infoContainer}>
        <View style={styles.infoRow}>
          <View style={styles.infoContainer}>
            <Text Text-16 numberOfLines={1}>
              {title}
            </Text>
            {status === 'PENDING' && (<View style={styles.infoRow}>
                <StatusIcon state="in-progress" width={16} height={16}/>
                <Spacer.W8 />
                <View style={styles.infoContainer}>
                  <Text TextThin-14 numberOfLines={1} StatusInformational>
                    Pending
                  </Text>
                </View>
              </View>)}
            {!hideSpaceUserAvatar && <SpaceUserAvatar xxSmall userId={pot?.userId} style={styles.spaceAvatar}/>}
          </View>
          <Spacer.W8 />
          <View style={styles.rightContent}>
            <BlurAmountView enabled={scrambled}>
              {iconInfo.isDeposit ? (<Text Numbers-16 Blue>
                  {formattedAmount}
                </Text>) : (<Text Numbers-16>{formattedAmount}</Text>)}
            </BlurAmountView>
            <Text TextThin-14 Gray>
              {formattedDate}
            </Text>
          </View>
        </View>
      </View>
    </View>);
    if (bounceCard) {
        return (<TouchableBounceCard onDark noMarginTop noMarginBottom onPress={handlePress} disabled={disabled} style={styles.bounceCard}>
        {content}
      </TouchableBounceCard>);
    }
    return (<TouchableHighlight hoverOnLight onCard={isWeb} activeOpacity={1} onPress={handlePress} underlayColor={colors.background.underlay} disabled={disabled}>
      {content}
    </TouchableHighlight>);
};
const styleSet = createStyleSheets((colors) => ({
    // On web the row is padded by 8 horizontally, so the card adds the other 8. The row
    // already has 16 above and below, so the card adds nothing vertically.
    bounceCard: {
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    itemContainer: {
        alignItems: 'center',
        flexDirection: 'row',
        paddingVertical: rem(16),
        paddingHorizontal: rem(16),
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    iconContainer: {
        alignItems: 'center',
        marginRight: rem(16),
        justifyContent: 'center',
        width: rem(STOCK_ICON_SIZE),
        height: rem(STOCK_ICON_SIZE),
        borderRadius: rem(STOCK_ICON_SIZE),
        backgroundColor: colors.background.dark,
    },
    emoji: {
        fontFamily: 'System',
        textAlign: 'center',
        color: 'black',
    },
    infoContainer: {
        flex: 1,
    },
    infoRow: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    rightContent: {
        alignItems: 'flex-end',
    },
    spaceAvatar: {
        paddingTop: rem(2),
    },
}));
export default memo(PotActivity);
